export type GeminiLiveSessionData = {
  value: string;
  model: string;
  instructions: string;
  tools: any[];
};

export type GeminiLiveCallbacks = {
  onReady: () => void;
  onAudio: () => void;
  onInputTranscript: (text: string, finished: boolean) => void;
  onOutputTranscript: (text: string, finished: boolean) => void;
  onTurnComplete: () => void;
  onInterrupted: () => void;
  onToolCall: (call: any) => void;
  onToolCallCancellation: (ids: string[]) => void;
  onUsage: (usage: any) => void;
  onError: (message: string) => void;
  onClose: () => void;
};

const GEMINI_LIVE_URL = "wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1beta.GenerativeService.BidiGenerateContentConstrained";

function bytesToBase64(bytes: Uint8Array) {
  let binary = "";
  for (let index = 0; index < bytes.length; index += 1) binary += String.fromCharCode(bytes[index]);
  return btoa(binary);
}

function base64ToBytes(value: string) {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

function pcm16Base64(samples: Float32Array, sourceRate: number) {
  const targetRate = 16_000;
  const ratio = sourceRate / targetRate;
  const length = Math.max(1, Math.floor(samples.length / ratio));
  const pcm = new Int16Array(length);
  for (let index = 0; index < length; index += 1) {
    const start = Math.floor(index * ratio);
    const end = Math.max(start + 1, Math.floor((index + 1) * ratio));
    let total = 0;
    for (let sampleIndex = start; sampleIndex < Math.min(end, samples.length); sampleIndex += 1) total += samples[sampleIndex];
    const sample = Math.max(-1, Math.min(1, total / Math.max(1, end - start)));
    pcm[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return bytesToBase64(new Uint8Array(pcm.buffer));
}

export class GeminiLiveConnection {
  private socket: WebSocket | null = null;
  private audioContext: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private silentGain: GainNode | null = null;
  private playbackTime = 0;
  private playbackSources = new Set<AudioBufferSourceNode>();
  private closed = false;

  constructor(private callbacks: GeminiLiveCallbacks) {}

  connect(session: GeminiLiveSessionData, stream: MediaStream, history: Array<{ role: string; content: string }>, audioContext: AudioContext) {
    return new Promise<void>((resolve, reject) => {
      let ready = false;
      const socket = new WebSocket(`${GEMINI_LIVE_URL}?access_token=${encodeURIComponent(session.value)}`);
      this.socket = socket;
      socket.addEventListener("open", () => {
        this.send({
          setup: {
            model: `models/${session.model}`,
            generationConfig: {
              responseModalities: ["AUDIO"],
              speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: "Puck" } } },
            },
            systemInstruction: { parts: [{ text: session.instructions }] },
            tools: [{ functionDeclarations: session.tools }],
            inputAudioTranscription: {},
            outputAudioTranscription: {},
            historyConfig: { initialHistoryInClientContent: true },
            realtimeInputConfig: {
              automaticActivityDetection: { disabled: false, prefixPaddingMs: 300, silenceDurationMs: 800 },
              activityHandling: "START_OF_ACTIVITY_INTERRUPTS",
              turnCoverage: "TURN_INCLUDES_ONLY_ACTIVITY",
            },
          },
        });
      });
      socket.addEventListener("message", (event) => {
        void this.handleMessage(event, stream, history, audioContext, () => {
          ready = true;
          resolve();
        });
      });
      socket.addEventListener("error", () => {
        if (ready && !this.closed) this.callbacks.onError("The Gemini voice connection encountered an error.");
        reject(new Error("Gemini could not establish the voice connection."));
      });
      socket.addEventListener("close", () => {
        if (!ready) reject(new Error("Gemini closed the voice connection before setup completed."));
        if (ready && !this.closed) this.callbacks.onClose();
      });
    });
  }

  sendToolResponse(call: any, response: any) {
    this.send({
      toolResponse: {
        functionResponses: [{ id: call.id, name: call.name, response }],
      },
    });
  }

  close() {
    this.closed = true;
    this.stopPlayback();
    this.processor?.disconnect();
    this.source?.disconnect();
    this.silentGain?.disconnect();
    void this.audioContext?.close();
    this.socket?.close();
    this.socket = null;
    this.audioContext = null;
    this.processor = null;
    this.source = null;
    this.silentGain = null;
  }

  private send(message: any) {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(message));
  }

  private async handleMessage(event: MessageEvent, stream: MediaStream, history: Array<{ role: string; content: string }>, audioContext: AudioContext, resolve: () => void) {
    try {
      const raw = event.data instanceof Blob ? await event.data.text() : String(event.data);
      if (this.closed) return;
      const message = JSON.parse(raw);
      if (message.setupComplete) {
        if (history.length) {
          this.send({
            clientContent: {
              turns: history.map((turn) => ({
                role: turn.role === "assistant" ? "model" : "user",
                parts: [{ text: turn.content }],
              })),
              turnComplete: true,
            },
          });
        }
        this.startMicrophone(stream, audioContext);
        this.callbacks.onReady();
        resolve();
      }

      const content = message.serverContent;
      for (const part of content?.modelTurn?.parts || []) {
        if (part.inlineData?.data) {
          this.playAudio(part.inlineData.data);
          this.callbacks.onAudio();
        }
      }
      if (content?.inputTranscription) {
        this.callbacks.onInputTranscript(content.inputTranscription.text || "", content.inputTranscription.finished === true);
      }
      if (content?.outputTranscription) {
        this.callbacks.onOutputTranscript(content.outputTranscription.text || "", content.outputTranscription.finished === true);
      }
      if (content?.interrupted) {
        this.stopPlayback();
        this.callbacks.onInterrupted();
      }
      if (content?.turnComplete) this.callbacks.onTurnComplete();
      for (const call of message.toolCall?.functionCalls || []) this.callbacks.onToolCall(call);
      if (message.toolCallCancellation?.ids) this.callbacks.onToolCallCancellation(message.toolCallCancellation.ids);
      if (message.usageMetadata) this.callbacks.onUsage(message.usageMetadata);
    } catch {
      this.callbacks.onError("The Gemini voice session sent an unreadable event.");
    }
  }

  private startMicrophone(stream: MediaStream, audioContext: AudioContext) {
    const source = audioContext.createMediaStreamSource(stream);
    const processor = audioContext.createScriptProcessor(4096, 1, 1);
    const silentGain = audioContext.createGain();
    silentGain.gain.value = 0;
    processor.onaudioprocess = (event) => {
      if (this.closed || this.socket?.readyState !== WebSocket.OPEN) return;
      const data = pcm16Base64(event.inputBuffer.getChannelData(0), audioContext.sampleRate);
      this.send({ realtimeInput: { audio: { data, mimeType: "audio/pcm;rate=16000" } } });
    };
    source.connect(processor);
    processor.connect(silentGain);
    silentGain.connect(audioContext.destination);
    this.audioContext = audioContext;
    this.source = source;
    this.processor = processor;
    this.silentGain = silentGain;
    void audioContext.resume();
  }

  private playAudio(encodedAudio: string) {
    const context = this.audioContext;
    if (!context) return;
    const bytes = base64ToBytes(encodedAudio);
    const samples = new Float32Array(Math.floor(bytes.byteLength / 2));
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    for (let index = 0; index < samples.length; index += 1) samples[index] = view.getInt16(index * 2, true) / 0x8000;
    const buffer = context.createBuffer(1, samples.length, 24_000);
    buffer.copyToChannel(samples, 0);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    source.addEventListener("ended", () => this.playbackSources.delete(source));
    this.playbackSources.add(source);
    this.playbackTime = Math.max(context.currentTime, this.playbackTime);
    source.start(this.playbackTime);
    this.playbackTime += buffer.duration;
  }

  private stopPlayback() {
    this.playbackSources.forEach((source) => {
      try { source.stop(); } catch { /* The source may already have ended. */ }
    });
    this.playbackSources.clear();
    this.playbackTime = this.audioContext?.currentTime || 0;
  }
}
